import { useState } from "react";
import { useLocation } from "wouter";
import {
  Layers,
  Table,
  Shield,
  History,
  Bot,
  ExternalLink,
  X,
  Handshake,
  TicketCheck,
  FormInput,
  FileTerminal,
  ChevronDown,
  ChevronRight,
  Mails,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

const navigation = [
  {
    heading: "Forms",
    items: [
      { name: "Portal Forms", href: "/", icon: Table },
      { name: "Workflow Forms", href: "/workflowForms", icon: Layers },
      { name: "Partner Forms", href: "/partnerWorkflowForm", icon: Handshake },
      { name: "Adhoc Forms", href: "/adhocForm", icon: TicketCheck },
      { name: "Form Library", href: "/libraryForm", icon: FormInput },
    ],
  },
  {
    heading: "Templates",
    items: [
      { name: "Letter Template", href: "/lettertemplate", icon: FileTerminal },
      { name: "Email Template", href: "/emailtemplate", icon: Mails },
      { name: "Survey Template", href: "/surveytemplate", icon: Mails },
    ],
  },
  {
    heading: null,
    items: [
      { name: "Data Quality", href: "/quality", icon: Shield },
      { name: "Audit Log", href: "/audit", icon: History },
      { name: "AI Console", href: "/ai-builder", icon: Bot },
    ],
  },
];

export function Sidebar({ open, onToggle, isMobile }: SidebarProps) {
  const [location, setLocation] = useLocation();

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Forms: true,
    Templates: true,
  });

  const handleNavClick = (href: string) => {
    setLocation(href);

    if (isMobile) {
      onToggle();
    }
  };

  const toggleGroup = (heading: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [heading]: !prev[heading],
    }));
  };

  return (
    <aside
      className={cn(
        "bg-white border-r border-gray-200 shadow-sm transition-all duration-300 flex flex-col",
        isMobile
          ? "fixed left-0 top-0 h-full z-50 w-64" +
          (open ? " translate-x-0" : " -translate-x-full")
          : (open ? "w-64" : "w-16") + " sticky top-0 h-full"
      )}
    >
      {/* Mobile Header */}
      {isMobile && open && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Menu</h2>

          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="p-1"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}

      {/* Main Content */}
      <div
        className={cn(
          "flex-1 overflow-y-auto p-4",
          isMobile && open ? "pt-0" : ""
        )}
      >
        {/* Back to BPRM Button */}
        {(open || isMobile) && (
          <a
            href="https://bprm-ai-portal.workfinity.com/hexagonDesign"
            className="mb-4 flex items-center justify-center gap-2 rounded-lg border border-workfinity-200 bg-workfinity-50 px-3 py-2 text-sm font-medium text-workfinity-700 hover:bg-workfinity-100 transition"
          >
            <ExternalLink className="h-4 w-4" />
            Back to BPRM
          </a>
        )}

        {/* Navigation */}
        <nav className="space-y-3">
          {navigation.map((group, index) => {
            if (group.heading) {
              const isOpen = openGroups[group.heading];

              return (
                <div key={group.heading}>
                  {(open || isMobile) && (
                    <button
                      onClick={() => toggleGroup(group.heading!)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <span>{group.heading}</span>

                      {isOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  )}

                  {isOpen && (
                    <div className="ml-2 mt-1 space-y-1">
                      {group.items.map((item) => {
                        const isActive = location === item.href;

                        return (
                          <button
                            key={item.name}
                            onClick={() => handleNavClick(item.href)}
                            className={cn(
                              "w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors",
                              isActive
                                ? "bg-workfinity-50 text-workfinity-700 border border-workfinity-200"
                                : "text-gray-700 hover:bg-gray-100"
                            )}
                            title={!open && !isMobile ? item.name : undefined}
                          >
                            <item.icon className="h-5 w-5 flex-shrink-0" />

                            {(open || isMobile) && (
                              <span className="text-sm">{item.name}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={`standalone-${index}`} className="space-y-1">
                {group.items.map((item) => {
                  const isActive = location === item.href;

                  return (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item.href)}
                      className={cn(
                        "w-full flex items-center space-x-3 px-1 py-2 rounded-lg text-left transition-colors relative",
                        isActive
                          ? "bg-workfinity-50 text-workfinity-700 border border-workfinity-200"
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                      title={!open && !isMobile ? item.name : undefined}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />

                      {(open || isMobile) && (
                        <span className="text-sm">{item.name}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Footer */}
      {(open || isMobile) && (
        <div className="border-t border-gray-200 p-4 bg-white mt-auto">
          <div className="text-sm text-gray-500 space-y-2">
            <div className="pt-2 border-t border-gray-200">
              <a
                href="https://ipcs-bprm.workfinity.com/login.aspx?encrypt=1&key=NjYwZjRhMzYtNzJjZi00YzViLWIxOTAtOTBlNmRmZmU2Mzg0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-workfinity-600 hover:text-workfinity-700 text-xs underline"
              >
                <span>Legacy System</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>

          </div>
        </div>
      )}
    </aside>
  );
}